/** The two request styles My Tools shows for Bedrock: OpenAI Chat Completions, which most people already know, and Bedrock's own Converse. */
export type ApiStyle = "chat" | "converse";
export type Language = "curl" | "python";

export const ApiStyles: ApiStyle[] = ["chat", "converse"];
export const Languages: Language[] = ["curl", "python"];

/** The variable a Bedrock API key goes in. Bedrock's SDKs read it from there, so the examples do too. */
export const KeyVariable = "AWS_BEARER_TOKEN_BEDROCK";

export const CompatibilityUrl = "https://docs.aws.amazon.com/bedrock/latest/userguide/models-api-compatibility.html";
export const AvailabilityUrl = "https://docs.aws.amazon.com/bedrock/latest/userguide/models-endpoint-availability.html";

/** The path a style is served at on bedrock-runtime. */
export function apiStylePath(style: ApiStyle, model: string): string {
  return style === "chat" ? "/openai/v1/chat/completions" : `/model/${model}/converse`;
}

/** The AWS region in a bedrock-runtime address, or null for an address of another shape. */
export function regionOf(endpoint: string): string | null {
  return /bedrock-runtime(?:-fips)?\.([a-z0-9-]+)\.amazonaws\.com/.exec(endpoint)?.[1] ?? null;
}

/** A complete request in one style and language, with the person's endpoint and model filled in and the key read from its variable. */
export function apiExample(style: ApiStyle, language: Language, endpoint: string, model: string, region: string): string {
  if (style === "chat") {
    return language === "curl"
      ? [
          `curl "${endpoint}${apiStylePath("chat", model)}" \\`,
          `  -H "Authorization: Bearer $${KeyVariable}" \\`,
          `  -H "Content-Type: application/json" \\`,
          `  -d '{`,
          `    "model": "${model}",`,
          `    "messages": [{"role": "user", "content": "Hello"}]`,
          `  }'`,
        ].join("\n")
      : [
          `import os`,
          `from openai import OpenAI`,
          ``,
          `client = OpenAI(`,
          `    base_url="${endpoint}/openai/v1",`,
          `    api_key=os.environ["${KeyVariable}"],`,
          `)`,
          `response = client.chat.completions.create(`,
          `    model="${model}",`,
          `    messages=[{"role": "user", "content": "Hello"}],`,
          `)`,
          `print(response.choices[0].message.content)`,
        ].join("\n");
  }

  return language === "curl"
    ? [
        `curl "${endpoint}${apiStylePath("converse", model)}" \\`,
        `  -H "Authorization: Bearer $${KeyVariable}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '{`,
        `    "messages": [{"role": "user", "content": [{"text": "Hello"}]}]`,
        `  }'`,
      ].join("\n")
    : [
        `# boto3 reads your key from ${KeyVariable}.`,
        `import boto3`,
        ``,
        `client = boto3.client("bedrock-runtime", region_name="${region}")`,
        `response = client.converse(`,
        `    modelId="${model}",`,
        `    messages=[{"role": "user", "content": [{"text": "Hello"}]}],`,
        `)`,
        `print(response["output"]["message"]["content"][0]["text"])`,
      ].join("\n");
}
